import { Tab, Tabs, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  MatchingRoundId,
  MatchingRoundStatus,
} from '../../api/matching-rounds';
import { getRoutePath, Pages } from '../../router/constants';
import { COLORS } from '../../theme/variables';
import cn from 'classnames';

interface TabsProps {
  tooltip?: {
    title: string;
  };
  props: {
    label: string;
    value?: any;
    component?: React.ReactNode;
    to?: any;
    disabled?: boolean;
    ['data-testid']?: string;
  };
}

interface PageTabsProps {
  roundId?: MatchingRoundId;
  roundStatus?: MatchingRoundStatus;
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '0',
    boxShadow: `0px 1px 0px ${COLORS.COLOR_GRAY_LIGHTENED_30}`,

    '& .MuiTab-root + .MuiTab-root': {
      marginLeft: 20,

      [theme.breakpoints.up('xs')]: {
        marginLeft: 32,
      },
    },
  },
  tooltipWrapper: {
    padding: '0',
    '& > button': {
      whiteSpace: 'nowrap',
    },
  },
}));

function MatchingRoundPageTabs({ roundId, roundStatus }: PageTabsProps) {
  const location = useLocation();
  const classes = useStyles();

  const isHeatmapDisabled = roundStatus === MatchingRoundStatus.CREATED;

  const tabs: TabsProps[] = useMemo(
    () => [
      {
        props: {
          label: 'Details',
          'data-testid': 'round-nav-details',
          component: Link,
          ...(roundId
            ? {
              to: getRoutePath(Pages.MATCHING_ROUND_DETAILS, { roundId }),
              value: getRoutePath(Pages.MATCHING_ROUND_DETAILS, { roundId }),
            }
            : {
              to: Pages.NEW_MATCHING_ROUND,
              value: Pages.NEW_MATCHING_ROUND,
            }),
        },
      },
      {
        ...(!roundId
          ? {
            tooltip: {
              title: `Please complete creation of matching round\nbefore switching to Subgroups`,
            },
          }
          : {}),
        props: {
          label: 'Subgroups',
          'data-testid': 'round-nav-subgroups',
          disabled: !roundId,
          ...(roundId
            ? {
              component: Link,
              to: getRoutePath(Pages.MATCHING_ROUND_SUBGROUPS, { roundId }),
              value: getRoutePath(Pages.MATCHING_ROUND_SUBGROUPS, { roundId }),
            }
            : {}),
        },
      },
      {
        ...(!roundId || isHeatmapDisabled
          ? {
            tooltip: {
              title: !roundId
                ? `Please complete creation of matching round\nbefore switching to Heatmap`
                : `Please run simulation\nbefore switching to Heatmap`,
            },
          }
          : {}),
        props: {
          label: 'Heatmap',
          'data-testid': 'round-nav-heatmap',
          disabled: !roundId || isHeatmapDisabled,
          ...(roundId
            ? {
              component: Link,
              to: getRoutePath(Pages.MATCHING_ROUND_HEATMAP, { roundId }),
              value: getRoutePath(Pages.MATCHING_ROUND_HEATMAP, { roundId }),
            }
            : {}),
        },
      },
      {
        ...(!roundId
          ? {
            tooltip: {
              title: `Please complete creation of matching round\nbefore switching to Logs`,
            },
          }
          : {}),
        props: {
          label: 'Logs',
          'data-testid': 'round-nav-logs',
          disabled: !roundId,
          ...(roundId
            ? {
              component: Link,
              to: getRoutePath(Pages.MATCHING_ROUND_LOGS, { roundId }),
              value: getRoutePath(Pages.MATCHING_ROUND_LOGS, { roundId }),
            }
            : {}),
        },
      },
    ],
    [roundId, isHeatmapDisabled],
  );

  return (
    <div className={classes.container}>
      <Tabs
        value={location.pathname}
        indicatorColor='primary'
        textColor='primary'
        variant='scrollable'
        scrollButtons='on'>
        {tabs.map((tab) =>
          tab.tooltip ? (
            <Tooltip title={tab.tooltip.title} key={tab.props.label}>
              <span className={cn(classes.tooltipWrapper, 'MuiTab-root')}>
                <Tab {...tab.props} />
              </span>
            </Tooltip>
          ) : (
            <Tab {...tab.props} key={tab.props.label} />
          ),
        )}
      </Tabs>
    </div>
  );
}

export default MatchingRoundPageTabs;
